import { useEffect, useState, type ReactNode } from "react";

/** Where the rail's open/closed state is kept, so it survives a reload and a sign-out. */
const NAV_KEY = "complao.nav.collapsed";

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(NAV_KEY) === "1";
  } catch {
    /* Storage can be denied outright — a rail that will not open is worse than one
       that forgets, so an unreadable store means open. */
    return false;
  }
}

/**
 * The application chrome, as the prototype screens draw it.
 *
 * A Deep Forest bar carrying the mark, the lowercase wordmark and the signed-in
 * principal; a Forest sidebar; and a main column capped at 1180px. Every screen from 01
 * onwards sits inside this, so it lives here rather than being redrawn per view.
 *
 * The `who` block names the principal *and* their standing — "Applicant · Not yet
 * verified" in the prototype. Who you are and whether you have been verified are the two
 * facts that decide what the rest of the interface will let you do, so they are on screen
 * rather than implied.
 *
 * `mark` is the certification body's own, once it has uploaded one. ADR 0007 gives the body
 * its mark and gives Complao the layout, so the tile is the one thing that changes and the
 * `complao` wordmark beside it does not — a body signed into its own workspace should see
 * itself, and should still be able to tell whose platform it is on. Every prototype screen
 * draws the generic `c` because the prototype has no tenant to draw instead.
 *
 * **The rail collapses, and collapsing hides it rather than narrowing it.** Its items are
 * words — "Competence record", "Verification queue" — and an icon rail would mean inventing
 * a glyph for each, which is a second vocabulary to learn and one the prototype does not
 * draw. So the closed state gives the whole track to the page. The toggle therefore cannot
 * live in the rail, because a control inside the thing it hides cannot bring it back: it
 * sits in the masthead, which is on every screen the rail is on. The choice is remembered
 * across screens and reloads — an auditor who wants the width wants it on the next screen
 * too, and a rail that reopened itself each navigation would be a rail that does not
 * collapse.
 */
export function AppShell({
  who,
  role,
  navLabel,
  nav,
  mark = null,
  markAlt,
  onSignOut,
  children,
}: {
  who: string;
  role: string;
  navLabel: string;
  nav: ReactNode;
  /** An object URL for the stored mark, or null while there is none. */
  mark?: string | null;
  markAlt?: string;
  onSignOut?: () => void;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      window.localStorage.setItem(NAV_KEY, collapsed ? "1" : "0");
    } catch {
      /* Nothing to do — the rail still works, it just forgets. */
    }
  }, [collapsed]);

  return (
    <>
      <header className="top">
        <button
          type="button"
          className="top__nav"
          aria-expanded={!collapsed}
          aria-controls="app-nav"
          aria-label={collapsed ? "Show navigation" : "Hide navigation"}
          title={collapsed ? "Show navigation" : "Hide navigation"}
          onClick={() => setCollapsed((was) => !was)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M2 4h12M2 8h12M2 12h12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
        {mark ? (
          <div className="mark mark--brand">
            <img src={mark} alt={markAlt ?? ""} />
          </div>
        ) : (
          <div className="mark" aria-hidden="true">
            c
          </div>
        )}
        <div>
          <div className="wm">complao</div>
          <div className="tag">AUDIT PLATFORM</div>
        </div>
        <div className="who">
          <b>{who}</b>
          {role}
        </div>
        {onSignOut ? (
          <button type="button" className="top__out" onClick={onSignOut}>
            Sign out
          </button>
        ) : null}
      </header>

      <div className={collapsed ? "layout layout--narrow" : "layout"}>
        <nav className="side" id="app-nav" hidden={collapsed}>
          <div className="lbl">{navLabel}</div>
          {nav}
        </nav>
        <main>{children}</main>
      </div>
    </>
  );
}
